/**
 * Unified environment configuration system for all frontend projects.
 * This module provides a consistent way to access environment variables
 * across all projects (admin, my, portal, widget).
 */

// Add type declarations for environments
declare global {
  interface ImportMeta {
    env: Record<string, any>;
  }

  // Add Node.js process if not already declared
  var process: {
    env?: Record<string, string>;
  };
}

/**
 * Environment configuration interface
 */
export interface IConfig {
  apiUrl: string | undefined;
  apiDomain: string | undefined;
  cdnUrl: string | undefined;
  cdnDomain: string | undefined;
  authUrl: string | undefined;
  authDomain: string | undefined;
  myUrl: string | undefined;
  myDomain: string | undefined;
  portalUrl: string | undefined;
  portalDomain: string | undefined;
  adminUrl: string | undefined;
  adminDomain: string | undefined;
  authRealm: string;
  authClientId: string;
  authClientSecret: string;
  mode: string;
  environment: "development" | "acceptance" | "production";
  debug: boolean;
  version: string;
}

/**
 * Removes trailing slash from a domain string if present
 * @param domain The domain string to normalize
 * @returns The domain without trailing slash or undefined if input is falsy
 */
const normalizeDomain = (domain?: string): string | undefined => {
  if (!domain) return undefined;
  return domain.endsWith("/") ? domain.slice(0, -1) : domain;
};

const API_DOMAIN = normalizeDomain(import.meta.env.VITE_APP_API_DOMAIN);
const CDN_DOMAIN = normalizeDomain(import.meta.env.VITE_APP_CDN_DOMAIN);
const AUTH_DOMAIN = normalizeDomain(import.meta.env.VITE_APP_AUTH_DOMAIN);
const MY_DOMAIN = normalizeDomain(import.meta.env.VITE_APP_MY_DOMAIN);
const PORTAL_DOMAIN = normalizeDomain(import.meta.env.VITE_APP_PORTAL_DOMAIN);
const ADMIN_DOMAIN = normalizeDomain(import.meta.env.VITE_APP_ADMIN_DOMAIN);

/**
 * Creates a URL from a domain
 * @param domain The domain to create a URL from
 * @returns A full URL with https:// protocol or undefined if domain is falsy
 */
const createUrl = (domain?: string): string | undefined => {
  return domain ? `https://${domain}` : undefined;
};

const DEFAULT_CONFIG: IConfig = {
  apiUrl: createUrl(API_DOMAIN),
  apiDomain: API_DOMAIN,
  cdnUrl: createUrl(CDN_DOMAIN),
  cdnDomain: CDN_DOMAIN,
  authUrl: createUrl(AUTH_DOMAIN),
  authDomain: AUTH_DOMAIN,
  myUrl: createUrl(MY_DOMAIN),
  myDomain: MY_DOMAIN,
  portalUrl: createUrl(PORTAL_DOMAIN),
  portalDomain: PORTAL_DOMAIN,
  adminUrl: createUrl(ADMIN_DOMAIN),
  adminDomain: ADMIN_DOMAIN,
  authRealm: "RumbleDirect",
  authClientId: import.meta.env.VITE_APP_AUTH_CLIENT_ID,
  authClientSecret: import.meta.env.VITE_APP_AUTH_CLIENT_SECRET,
  mode: import.meta.env.MODE || "development",
  environment: "development",
  debug: true,
  version: "0.0.0",
};

let cachedConfig: IConfig | null = null;

/**
 * Reads environment variables based on the current environment (browser vs Node.js)
 */
function readEnvVars(): Record<string, string> {
  const env: Record<string, string> = {};

  // Node.js environment
  if (typeof process !== "undefined" && process.env) {
    Object.assign(env, process.env);
  }

  // Browser environment - window.__env
  if (typeof window !== "undefined" && (window as any).__env) {
    Object.assign(env, (window as any).__env);
  }

  // Vite environment variables
  if (typeof import.meta !== "undefined" && import.meta.env) {
    // Convert all environment variables to strings
    Object.entries(import.meta.env).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        env[key] = String(value);
      }
    });
  }

  return env;
}

/**
 * Normalizes environment variable names
 * Handles different formats: VITE_APP_API_URL, etc.
 */
function normalizeEnvVar(
  name: string,
  env: Record<string, string>,
): string | undefined {
  // Common environment variable prefixes in different frameworks
  const prefixes = ["", "VITE_", "VITE_APP_"];

  // Try each prefix
  for (const prefix of prefixes) {
    const fullName = `${prefix}${name}`;
    if (fullName in env) {
      return env[fullName];
    }
  }

  return undefined;
}

/**
 * Gets environment configuration with defaults
 */
export function getConfig(): IConfig {
  if (cachedConfig) {
    return cachedConfig;
  }

  const env = readEnvVars();

  const config: IConfig = {
    ...DEFAULT_CONFIG,
    environment:
      (normalizeEnvVar("NODE_ENV", env) as
        | "development"
        | "acceptance"
        | "production") || DEFAULT_CONFIG.environment,
    debug:
      normalizeEnvVar("DEBUG", env) === "true" ||
      DEFAULT_CONFIG.environment === "development",
    version: normalizeEnvVar("VERSION", env) || DEFAULT_CONFIG.version,
  };

  cachedConfig = config;
  return config;
}

/**
 * Gets a specific environment variable by key
 * @param key The key of the environment variable
 */
export function getConfigValue<T extends keyof IConfig>(key: T): IConfig[T] {
  const config = getConfig();
  if (key in config) {
    return config[key];
  }
  console.warn(`Config key "${key}" not found in environment configuration.`);
  return undefined as IConfig[T];
}
